import { useMessage } from '@common/hooks/message';
import * as React from 'react';
import { FolderPreviewManagementFormat, FolderPreviewManagementProps } from './folder-preview-management.types';
import { HttpErrorDto } from '@common/dto/http';
import { I18n } from 'react-redux-i18n';

export const useController = (props: FolderPreviewManagementProps) => {
  const {
    showErrorMessage,
    messageProps,
    closeMessage,
  } = useMessage();
  const { onSubmit, getFolders, createFolder, deleteFolder, getFormats, deleteFormat, mode, submitOnSelect } = props;

  const [folders, setFolders] = React.useState<Array<{ id: number; name: string; }>>([]); // TODO use FolderPrimaryListDto once merged branch
  const [folderPickerOpened, setFolderPickerOpened] = React.useState(false);
  const [formats, setFormats] = React.useState<Array<FolderPreviewManagementFormat>>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<{ id: number; name: string; } | undefined>();
  const [selectedFormat, setSelectedFormat] = React.useState<{ id: number; name: string; } | undefined>();
  
  const submit = React.useCallback(async ({ format, folder }: { format: { name: string; id?: string | number }; folder: { id: string | number; name: string } }) => {
      try {
        await onSubmit({ folder, format });
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Operation failed'));
      }
  }, [showErrorMessage, onSubmit]);

  const handleSubmit = React.useCallback(async (params: { name: string }) => {
    if (!selectedFolder) {
      return;
    }

    if (mode === 'create') {
      await submit({ folder: selectedFolder, format: { name: params.name } });
    }
    
    if (mode === 'edit' && selectedFormat) {
      await submit({ folder: selectedFolder, format: selectedFormat });
    }
  }, [onSubmit, selectedFolder, selectedFormat]);

  const handleFolderSelect = React.useCallback(async (format: { id: number; name: string; }, folder?: { id: number; name: string; }) => {
    setSelectedFolder(folder);
    setSelectedFormat(format);

    if (submitOnSelect && folder) {
      await submit({ folder, format });
    }

    setFolderPickerOpened(false);
  }, []);

  const getAndSetFolders = React.useCallback(async () => {
    const res = await getFolders();
    setFolders(res);
  }, [getFolders]);

  const handleFolderCreate = React.useCallback(async (data: { parentId?: number; name: string }) => {
    await createFolder({
      parentid: data.parentId,
      foldername: data.name, 
    });
    await getAndSetFolders();
  }, [createFolder, getAndSetFolders]);

  const handleFolderDelete = React.useCallback(async (folderId: number) => {
    await deleteFolder(folderId);
    await getAndSetFolders();
  }, [deleteFolder, getAndSetFolders]);

  const getAndSetFormats = React.useCallback(async (folderId: number) => {
    const res = await getFormats(folderId);
    setFormats(res);
  }, [getFormats]);

  const resetFormats = React.useCallback(() => {
    setFormats([]);
  }, []);

  const openFolderPicker = React.useCallback(() => {
    setFolderPickerOpened(true);
  }, []);

  const closeFolderPicker = React.useCallback(() => {
    setFolderPickerOpened(false);
    resetFormats();
  }, [resetFormats]);

  const handleFormatDelete = React.useCallback(async (folderId: number, formatId: number) => {
    await deleteFormat(folderId, formatId);
    await getAndSetFormats(folderId);
  }, [deleteFormat, getAndSetFolders]);
  
  const isAllowedToSubmitFormatOrFolderLoader = React.useCallback((params: { folderId: number; formatId?: number }) => {

    return mode === "edit"
      ? Boolean(params.formatId) && Boolean(params.folderId)
      : Boolean(params.folderId)
  
  }, [mode]);

  return {
    isAllowedToSubmitFormatOrFolderLoader,
    selectedFormat,
    handleFormatDelete,
    formats,
    getAndSetFormats,
    resetFormats,
    handleFolderDelete,
    handleFolderCreate,
    selectedFolder,
    folders,
    getAndSetFolders,
    handleFolderSelect,
    folderPickerOpened,
    closeFolderPicker,
    openFolderPicker,
    handleSubmit,
    showErrorMessage,
    messageProps,
    closeMessage,
  };
};